import React, { useState } from 'react'

import { path, useMobile } from '@wap-client/core'
import { useApp } from '@wap-client/core'
import { Row, Column } from '@/components/base/gridview'
import Anchor from '@/components/base/anchor'
import Icon from '@/components/base/icon'
import { NavigationReference } from '@wap-client/services'
import IconList from '@/components/base/icon-list'
import Image from '@/components/base/image'

import Panel from './Panel'
import { NavigationBreadcrumb, NavigationProps } from './types'
import SocialMedia from '../social-media'
import { GTMEvent } from '@/utils/gtm'
import { useRouter } from 'next/router'
import { IAuthUrl } from '@/utils/types'
import useAuthStore from '@/context/useAuth'
import Link from 'next/link'
import { Akiva } from '@/utils/akiva/akiva'
import { AkivaType } from '@/utils/akiva/types'
import useTranslations from '@/hooks/useTranslations'

const Navigation: React.FunctionComponent<NavigationProps> = ({
  data,
  isHideBrand,
  isOpen,
  logo,
  onClose,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<NavigationBreadcrumb[]>([])

  const app = useApp()
  const { translation } = useTranslations()
  const router = useRouter()
  const { authData } = useAuthStore()
  const isMobile = useMobile(app.headers['user-agent'])
  const profilePage = app.settings.routes?.['my-profile']?.href || 'my-profile'

  const onInteract = (page: string) => {
    let event = {}

    event = {
      page: page,
    }

    GTMEvent('menu', event, app)
    Akiva({
      app,
      type: AkivaType.Menu,
      data: { fullPath: page },
    })
  }

  const openMenu = (title: string, parentId: string, childId: string) => {
    const parentMenuList: NodeListOf<HTMLDivElement> =
      document.querySelectorAll(`[data-menu="${parentId}"]`)
    const parentMenu: HTMLDivElement = parentMenuList[0]

    parentMenu.style.left = '-50%'
    parentMenu.style.opacity = '0'
    parentMenu.style.zIndex = ''

    const childMenuList: NodeListOf<HTMLDivElement> = document.querySelectorAll(
      `[data-menu="${childId}"]`,
    )
    const childMenu: HTMLDivElement = childMenuList[0]

    childMenu.style.left = '0%'
    childMenu.style.opacity = '1'
    childMenu.style.zIndex = '10'

    setBreadcrumbs((state) => [
      ...state,
      {
        child: childId,
        parent: parentId,
        title,
      },
    ])
  }

  const closeMenu = (parentId: string, childId: string) => {
    const parentMenuList: NodeListOf<HTMLDivElement> =
      document.querySelectorAll(`[data-menu="${parentId}"]`)
    const parentMenu: HTMLDivElement = parentMenuList[0]

    parentMenu.style.left = '0%'
    parentMenu.style.opacity = '1'
    parentMenu.style.zIndex = '10'

    const childMenuList: NodeListOf<HTMLDivElement> = document.querySelectorAll(
      `[data-menu="${childId}"]`,
    )
    const childMenu: HTMLDivElement = childMenuList[0]

    childMenu.style.left = '50%'
    childMenu.style.opacity = '0'
    childMenu.style.zIndex = ''
  }

  const handlePreviousMenu = () => {
    const frame = breadcrumbs[breadcrumbs.length - 1]

    closeMenu(frame.parent, frame.child)
    setBreadcrumbs((state) => state.slice(0, state.length - 1))
  }

  const createMenuScene = (menu: NavigationReference[], parent?: string) => {
    let frames: JSX.Element[] = []
    const parentId = parent || 'main'

    const doms = menu.map((menuItem, index) => {
      const title = menuItem.title

      if (menuItem.children.length) {
        frames = [
          ...frames,
          ...createMenuScene(menuItem.children, menuItem.href),
        ]

        return (
          <li key={index} className={menuItem.className}>
            <Icon name="arrow-right" className="menu-item-arrow" size="small" />
            <Anchor
              onClick={() => {
                openMenu(title, parentId, menuItem.href)
              }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </li>
        )
      }

      return (
        <li key={index} className={menuItem.className}>
          {menuItem.href?.indexOf('http') === 0 ? (
            <a
              href={menuItem.href}
              target={menuItem.target}
              dangerouslySetInnerHTML={{ __html: title }}
              rel="noreferrer"
            />
          ) : (
            <Anchor
              onClick={() => {
                onInteract(path.url(app.environment, menuItem.href))
              }}
              href={menuItem.href}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
        </li>
      )
    })

    frames.push(
      <nav
        key={parentId}
        data-menu={parentId}
        className="navigation-menu"
        style={parent ? {} : { left: '0', opacity: 1, paddingTop: 0 }}
      >
        <ul>
          {doms}
          <li>
            {authData?.isAuth && authData?.user ? (
              <Anchor href={profilePage}>{translation('my-profile')}</Anchor>
            ) : (
              <Link href={`auth/${IAuthUrl.REDIRECT}`}>
                {translation('login')}
              </Link>
            )}
          </li>
        </ul>
      </nav>,
    )

    return frames
  }

  const renderNavs = () => {
    const frames = createMenuScene(data)

    return <div className="navigation-content scrollbar">{frames}</div>
  }

  return (
    <Panel onClose={onClose} isOpen={isOpen} logo={logo}>
      <div className="navigation">
        <header className="navigation-header">
          {breadcrumbs.length ? (
            <Row xs={{ align: 'center' }}>
              <Column>
                <button
                  className="navigation-back-button"
                  onClick={handlePreviousMenu}
                >
                  <Icon name="arrow-left" size="xsmall" />
                  <span>{breadcrumbs[breadcrumbs.length - 1].title}</span>
                </button>
              </Column>
            </Row>
          ) : null}
        </header>
        {renderNavs()}
        <footer className="navigation-footer">
          <Row
            xs={{ align: 'center', justify: 'center' }}
            lg={{ justify: 'center' }}
          >
            <Column xs={{ auto: true }}>
              <SocialMedia />
            </Column>
            {!isHideBrand && isMobile && (
              <Column xs={{ auto: true }}>
                <a
                  href="https://goturkiye.com"
                  className="navigation-footer-logo"
                >
                  <Image {...app.theme.logo} alt={app.page.title} />
                </a>
              </Column>
            )}
          </Row>
        </footer>
      </div>
    </Panel>
  )
}

export default Navigation
